import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  DatePicker,
  Button,
  Row,
  Col,
  Alert,
  Space,
  Empty,
  Card,
  Statistic,
} from "antd";
import { RedoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAdmin } from "context/adminContext";
import { useLoading } from "context/loadingContext";
import { ListTransactionFeeShares } from "functions/admin/feeShareRule";
import moneyFormat from "functions/utils/moneyFormat";
import TablePagination from "components/pagination/tablePagination";
import { bg_button, bg_shadow } from "constant/css";

const { RangePicker } = DatePicker;

const SplitHistoryPanel = () => {
  const { activeMerchantId } = useAdmin();
  const { showLoading, hideLoading } = useLoading();

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ byRule: [] });
  const [paginationData, setPaginationData] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  useEffect(() => {
    if (activeMerchantId) fetchShares();
    // eslint-disable-next-line
  }, [activeMerchantId, page, pageSize]);

  const fetchShares = async () => {
    if (!activeMerchantId) return;
    showLoading();
    try {
      const parts = [`merchantId=${activeMerchantId}`, `page=${page}`, `size=${pageSize}`];
      if (dateRange && dateRange[0] && dateRange[1]) {
        const from = dayjs(dateRange[0]).startOf("day").format("YYYY-MM-DD HH:mm:ss");
        const to = dayjs(dateRange[1]).endOf("day").format("YYYY-MM-DD HH:mm:ss");
        parts.push(`createdAt=${encodeURIComponent(`${from}->${to}`)}`);
      }
      const response = await ListTransactionFeeShares(parts.join("&"));
      if (response && response.success) {
        setData(response.data || []);
        setPaginationData(response.pagination || {});
        setSummary(response.summary || { byRule: [] });
      } else {
        setData([]);
        setSummary({ byRule: [] });
      }
    } finally {
      hideLoading();
    }
  };

  const refresh = () => {
    setPage(1);
    fetchShares();
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span className="text-xs">{v}</span>,
    },
    {
      title: "Settle",
      dataIndex: "merchantSettleId",
      width: 70,
      render: (v) => <Tag color="default">{v}</Tag>,
    },
    {
      title: "Гүйлгээ",
      dataIndex: "transactionId",
      width: 80,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: "Хүлээн авагч",
      dataIndex: "recipientName",
      render: (v, r) => (
        <div>
          <div className="text-xs font-medium">{v || "-"}</div>
          <div className="text-[10px] text-gray-500">
            {r.accountNumber} / {r.bankCode}
          </div>
        </div>
      ),
    },
    {
      title: "Эх fee",
      dataIndex: "merchantFee",
      align: "right",
      width: 110,
      render: (v) => (
        <span className="text-xs text-gray-500">{moneyFormat(parseFloat(v || 0))}₮</span>
      ),
    },
    {
      title: "%",
      dataIndex: "percent",
      align: "right",
      width: 70,
      render: (v) => (
        <Tag color="blue" className="text-xs">
          {parseFloat(v)}%
        </Tag>
      ),
    },
    {
      title: "Авсан",
      dataIndex: "amount",
      align: "right",
      width: 120,
      render: (v) => (
        <span className="text-xs font-semibold text-cyan-700">
          {moneyFormat(parseFloat(v || 0))}₮
        </span>
      ),
    },
    {
      title: "IncomeTx",
      dataIndex: "incomeTransactionId",
      width: 80,
      render: (v) => (v ? <Tag color="green">{v}</Tag> : <Tag>—</Tag>),
    },
    {
      title: "Огноо",
      dataIndex: "createdAt",
      width: 140,
      render: (v) => (
        <span className="text-xs">
          {v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"}
        </span>
      ),
    },
  ];

  if (!activeMerchantId) {
    return <Alert type="warning" message="Merchant сонгоно уу" />;
  }

  return (
    <div className={`p-3 bg-white rounded-xl ${bg_shadow}`}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <h2 className="font-semibold text-lg">Split түүх</h2>
          <span className="text-xs text-gray-500">
            Гүйлгээ тус бүрд яаж хуваарилагдсан snapshot
          </span>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(v) => setDateRange(v)}
              size="small"
              allowClear={false}
            />
            <Button
              icon={<RedoOutlined />}
              onClick={refresh}
              className={`${bg_button} text-white`}
            >
              Шинэчлэх
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Per-rule subtotals */}
      {summary.byRule && summary.byRule.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {summary.byRule.map((r) => (
            <Col key={r.feeShareRuleId} xs={24} sm={12} md={8} lg={6}>
              <Card size="small" className={`${bg_shadow}`}>
                <Statistic
                  title={
                    <div>
                      <div className="text-xs font-semibold">
                        {r.recipientName || "-"}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {r.accountNumber}
                      </div>
                    </div>
                  }
                  value={r.total}
                  precision={2}
                  suffix="₮"
                  valueStyle={{ color: "#0e7490", fontSize: 16 }}
                />
                <div className="text-[10px] text-gray-500">
                  {r.count} split-ийн нийлбэр
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: "max-content", y: "calc(100vh - 50vh)" }}
        locale={{
          emptyText: (
            <Empty description="Хуваарилалтын түүх хоосон байна" />
          ),
        }}
      />

      <TablePagination
        page={page}
        paginationData={paginationData}
        changePage={setPage}
        changeSize={(_, size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
};

export default SplitHistoryPanel;
